import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { DmsTextFieldMasked } from "dmsbasereact";

/**
 * Mask function for CPF (Cadastro de Pessoas Físicas).
 *
 * Formats the input into the CPF pattern: XXX.XXX.XXX-XX
 *
 * @param {string} value - The raw input value.
 * @returns {string} The masked CPF value.
 */
export const CpfMask = (value) => {
  value = value.replace(/\D/g, "");

  if (value.length <= 3) return value;
  if (value.length <= 6) return `${value.slice(0, 3)}.${value.slice(3)}`;
  if (value.length <= 9)
    return `${value.slice(0, 3)}.${value.slice(3, 6)}.${value.slice(6)}`;
  return `${value.slice(0, 3)}.${value.slice(3, 6)}.${value.slice(
    6,
    9
  )}-${value.slice(9, 11)}`;
};

/**
 * Validates whether a CPF is valid.
 *
 * - Removes non-numeric characters
 * - Checks for correct length
 * - Validates check digits
 *
 * @param {string} cpf - The CPF to validate.
 * @returns {boolean} True if valid, false otherwise.
 */
export const isValidCPF = (cpf) => {
  cpf = cpf.replace(/\D/g, "");

  if (cpf.length !== 11 || /^(\d)\1+$/.test(cpf)) return false;

  let sum = 0;
  for (let i = 0; i < 9; i++) sum += parseInt(cpf.charAt(i)) * (10 - i);
  let remainder = (sum * 10) % 11;
  if (remainder === 10 || remainder === 11) remainder = 0;
  if (remainder !== parseInt(cpf.charAt(9))) return false;

  sum = 0;
  for (let i = 0; i < 10; i++) sum += parseInt(cpf.charAt(i)) * (11 - i);
  remainder = (sum * 10) % 11;
  if (remainder === 10 || remainder === 11) remainder = 0;
  if (remainder !== parseInt(cpf.charAt(10))) return false;

  return true;
};

/**
 * Text field component for CPF input with built-in mask and validation.
 *
 * @param {Object} props - The component props.
 * @param {string} props.name - Input name.
 * @param {string} props.label - Input label.
 * @param {string} props.placeholder - Input placeholder.
 * @param {boolean} [props.validateOnBlur=true] - Whether to validate when leaving the field.
 * @param {boolean} [props.validateOnChange=false] - Whether to validate while typing.
 * @param {Function} [props.onValidationChange] - Optional callback triggered with the validation result (true/false).
 * @returns {JSX.Element} The rendered CPF input field.
 */
export const DmsTextFieldCpf = ({
  name = "cpf",
  label = "CPF",
  placeholder = "XXX.XXX.XXX-XX",
  validateOnBlur = true,
  validateOnChange = false,
  onValidationChange,
  value,
  inputProps = {},
  ...props
}) => {
  const [error, setError] = useState(false);
  const [helperText, setHelperText] = useState("");

  // Validate initial value on mount or when value changes externally
  useEffect(() => {
    if (value) {
      const valid = isValidCPF(value);
      setError(!valid);
      setHelperText(valid ? "" : "Invalid CPF");
      onValidationChange?.(valid);
    }
  }, [value, onValidationChange]);

  const handleValidation = (value) => {
    if (!value) {
      setError(false);
      setHelperText("");
      onValidationChange?.(true);
      return;
    }

    const valid = isValidCPF(value);
    setError(!valid);
    setHelperText(valid ? "" : "Invalid CPF");
    onValidationChange?.(valid);
  };

  const handleBlur = (event) => {
    if (validateOnBlur) handleValidation(event.target.value);
  };

  const handleChange = (event) => {
    if (validateOnChange) handleValidation(event.target.value);
    props.onChange?.(event);
  };

  return (
    <DmsTextFieldMasked
      name={name}
      label={label}
      placeholder={placeholder}
      maskFunction={CpfMask}
      inputProps={{ maxLength: 14, ...inputProps }}
      error={error}
      helperText={helperText}
      onBlur={handleBlur}
      onChange={handleChange}
      value={value}
      {...props}
    />
  );
};

DmsTextFieldCpf.propTypes = {
  name: PropTypes.string,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  validateOnBlur: PropTypes.bool,
  validateOnChange: PropTypes.bool,
  onValidationChange: PropTypes.func,
  inputProps: PropTypes.object,
  value: PropTypes.string,
  onChange: PropTypes.func,
};
